<template>
  <v-container>
    <!-- Split Header Layout -->
    <v-row class="mb-6 align-center px-4">
      <div class="d-flex align-center py-4">
        <div>
          <div class="text-h4 font-weight-black primary--text d-flex align-center">
            Productos
          </div>
          <div class="caption grey--text font-weight-medium">Gestión del catálogo maestro de productos</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      
      <!-- Action Buttons -->
      <div class="d-none d-md-flex align-center">
        <Can I="read" a="Product">
          <export-products :family="family" :line="line" :disabled="loading" class="mr-2" />
        </Can>
        <Can I="update" a="Product">
          <v-btn outlined color="primary" depressed @click="$router.push({name:'BatchUpdate'})" class="mr-2 px-4 shadow-sm">
            <v-icon left small>fas fa-tags</v-icon>
            Actualizar
          </v-btn>
        </Can>
        <Can I="create" a="Product">
          <v-btn outlined color="primary" depressed @click="$router.push({name:'ImportProducts'})" class="mr-2 px-4 shadow-sm">
            <v-icon left small>fas fa-file-import</v-icon>
            Importar
          </v-btn>
        </Can>
        <Can I="create" a="Product">
          <v-btn color="primary" depressed @click="createUpdate" class="px-6 shadow-sm">
            <v-icon left small>fas fa-plus</v-icon>
            Nuevo Producto
          </v-btn>
        </Can>
      </div>

      <!-- Mobile Actions -->
      <div class="d-md-none">
        <v-menu offset-y bottom left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon>
              <v-icon>fas fa-ellipsis-v</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <Can I="create" a="Product">
              <v-list-item @click="createUpdate">
                <v-list-item-icon><v-icon small>fas fa-plus</v-icon></v-list-item-icon>
                <v-list-item-title>Nuevo</v-list-item-title>
              </v-list-item>
            </Can>
            <Can I="update" a="Product">
              <v-list-item @click="$router.push({name:'BatchUpdate'})">
                <v-list-item-icon><v-icon small>fas fa-tags</v-icon></v-list-item-icon>
                <v-list-item-title>Actualizar</v-list-item-title>
              </v-list-item>
            </Can>
          </v-list>
        </v-menu>
      </div>
    </v-row>

    <!-- Filters Card -->
    <v-card outlined class="modern-card pa-6 mb-6">
      <v-row dense align="center">
        <v-col cols="12" md="3">
          <v-select
            v-model="family"
            :items="families"
            item-text="name"
            item-value="id"
            label="Filtrar por Familia"
            placeholder="Todas las Familias"
            outlined
            dense
            clearable
            hide-details="auto"
            class="modern-select"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="line"
            :items="lines"
            item-text="name"
            item-value="id"
            label="Filtrar por Línea"
            placeholder="Todas las Líneas"
            outlined
            dense
            clearable
            hide-details="auto"
            class="modern-select"
          />
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="search"
            @keyup="searchTimeOut"
            label="Búsqueda rápida"
            placeholder="Código, nombre..."
            prepend-inner-icon="fas fa-search"
            outlined
            dense
            hide-details="auto"
            clearable
            :disabled="loading"
            class="modern-search-field"
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card>

    <!-- Data Table Card -->
    <v-card outlined class="modern-card elevation-0 overflow-hidden">
      <v-data-table
        :headers="headers"
        :items="products"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        class="modern-table"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
        @update:options="fetch"
      >
        <template v-slot:item.sku="{ item }">
          <span class="caption font-weight-black grey--text text--darken-2">{{ item.sku }}</span>
        </template>

        <template v-slot:item.name="{ item }">
          <div class="d-flex flex-column py-2">
            <span class="body-2 font-weight-bold primary--text" style="cursor: pointer" @click="edit(item)">{{ item.name }}</span>
            <span class="caption grey--text mt-n1">{{ item.family_name }} / {{ item.line_name }}</span>
          </div>
        </template>

        <template v-slot:item.customer_name="{ item }">
          <v-chip v-if="!item.customer" x-small label dark color="grey" class="font-weight-bold">
            GENERAL
          </v-chip>
          <div v-else class="d-flex align-center">
            <v-icon x-small class="mr-1 grey--text">fas fa-building</v-icon>
            <span class="caption font-weight-medium">{{ item.customer_name }}</span>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <v-switch 
            v-model="item.status" 
            @change="updateStatus(item)" 
            :disabled="!$can('update','Product')"
            dense 
            inset 
            hide-details
            color="success"
            class="mt-0"
          ></v-switch>
        </template>

        <template v-slot:item.relbase_product_id="{ item }">
          <v-chip
            v-if="relbaseEnabled && item.relbase_product_id"
            x-small label color="#35BDC8" dark class="font-weight-bold"
          >
            <v-icon x-small left>mdi-check-circle</v-icon>
            Sincronizado
          </v-chip>
          <v-chip
            v-else-if="relbaseEnabled"
            x-small label outlined color="grey"
          >
            Sin sincronizar
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center">
            <v-btn icon small color="primary" @click.stop="edit(item)" :disabled="!$can('update','Product')">
              <v-icon small>fas fa-edit</v-icon>
            </v-btn>
            <v-menu offset-y bottom left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon small>fas fa-ellipsis-v</v-icon>
                </v-btn>
              </template>
              <v-list dense class="modern-list shadow-sm">
                <v-list-item v-if="$can('create', 'Product')" @click.stop="duplicate(item)">
                  <v-list-item-icon class="mr-2"><v-icon small>fas fa-clone</v-icon></v-list-item-icon>
                  <v-list-item-title class="caption font-weight-medium">Duplicar Producto</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import {fetchProducts, updateProductStatus,fetchFamily,fetchLines} from '@/api/products'
import CustomerComplete from '@/components/customers/CustomerComplete'
import ExportProducts from '@/components/products/products/ExportProducts'
import notifications from '@/utils/notifications'

export default {
  name: 'Products',
  components: { CustomerComplete, ExportProducts },
  data: () => ({
    products: [],
    search: '',
    timer: '',
    options: {},
    total: 0,
    loading: false,
    lines: [],
    families: [],
    family: '',
    line: '',
  }),
  computed: {
    relbaseEnabled() {
      return this.$store.getters.getCompany.relbase_integration_enabled
    },
    headers() {
      const base = [
        { text: 'Código', value: 'sku', align: 'start', width: '120', class: 'grey lighten-4' },
        { text: 'Descripción / Producto', value: 'name', align: 'start', class: 'grey lighten-4' },
        { text: 'Cliente Destino', value: 'customer_name', align: 'start', class: 'grey lighten-4' },
        { text: 'Estatus', value: 'status', align: 'center', width: '100', class: 'grey lighten-4', sortable: false },
        { text: '', value: 'actions', align: 'center', width: '100', class: 'grey lighten-4', sortable: false }
      ]
      if (this.relbaseEnabled) {
        base.splice(base.length - 1, 0, {
          text: 'Relbase', value: 'relbase_product_id', align: 'center', width: '130', class: 'grey lighten-4', sortable: false
        })
      }
      return base
    },
  },
  methods: {
    createUpdate() {
      this.$router.push({ name: 'ProductCreateUpdate', params: {} })
    },
    edit(item) {
      this.$router.push({ name: 'ProductCreateUpdate', params: { product: item.id } })
    },
    duplicate(item) {
      this.$router.push({ name: 'ProductCreateUpdate', params: { product: item.id, duplicate: true } })
    },
    async fetch() {
      this.loading = true
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      let sort = ''
      if (sortBy && sortBy[0]) {
        if (sortBy[0] == 'family_name') sort = 'family__name'
        else if (sortBy[0] == 'line_name') sort = 'line__name'
        else if (sortBy[0] == 'customer_name') sort = 'customer__name'
        else sort = sortBy[0]
      }
      
      const query = `ordering=${sortDesc && sortDesc[0] ? sort : '-' + sort}&page=${page || 1}&page_size=${itemsPerPage || 20}&search=${this.search || ''}&family=${this.family || ''}&line=${this.line || ''}`
      
      try {
        const data = await fetchProducts(query)
        this.total = data.count
        this.products = data.results
      } catch (err) {
        notifications.showError('Error cargando productos: ' + err)
      } finally {
        this.loading = false
      }
    },
    async updateStatus(item) {
      try {
        await updateProductStatus(item.id, item.status)
        notifications.showSuccess('Estado de ' + item.name + ' actualizado')
      } catch (e) {
        notifications.showError('Error actualizando estado: ' + e)
        // Revert status on error
        item.status = !item.status
      }
    },
    searchTimeOut() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (this.options) this.options.page = 1
        this.fetch()
      }, 800);
    },
  },
  watch: {
    family() {
      if (this.options) this.options.page = 1
      this.fetch()
    },
    line() {
      if (this.options) this.options.page = 1
      this.fetch()
    }
  },
  async mounted() {
    this.lines = this.$store.getters.getLines
    this.families = this.$store.getters.getFamilies
    this.fetch()
  }
}
</script>

<style scoped>
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 12px !important;
}
.modern-table {
  background: transparent !important;
}
.shadow-sm {
  box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important;
}
.lh-1 { line-height: 1.2; }
</style>